// ============================================================
//  src/hooks/useProducts.js
//
//  Fetches price overrides from Supabase `price_overrides` table
//  and merges them over the base product data.
//
//  ── Supabase table required ──────────────────────────────────
//  Run this SQL once in your Supabase SQL editor:
//
//  create table price_overrides (
//    product_id  integer primary key,
//    prices      jsonb not null,
//    updated_at  timestamptz default now()
//  );
//
//  -- Allow public read (storefront reads prices without login)
//  alter table price_overrides enable row level security;
//  create policy "public read" on price_overrides
//    for select using (true);
//
//  -- Allow only authenticated service role to write
//  -- (Admin panel uses supabase directly — no extra policy needed
//  --  if you use the service role key in AdminPanel, or add:)
//  create policy "admin write" on price_overrides
//    for all using (true) with check (true);
// ============================================================

import { useState, useEffect } from 'react';
import baseProducts, { categories as baseCategories } from '../data/products';
import { supabase } from '../utils/supabase';

// ── Merge Supabase overrides on top of base product data ─────
function mergeWithOverrides(base, overrides) {
  if (!overrides || overrides.length === 0) return base;
  const map = {};
  overrides.forEach((row) => { map[row.product_id] = row.prices; });
  return base.map((product) => {
    const prices = map[product.id];
    if (!prices) return product;
    return {
      ...product,
      prices,
      price: Object.values(prices)[0], // keep `price` in sync with first size
    };
  });
}

export default function useProducts() {
  const [products, setProducts] = useState(baseProducts);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let cancelled = false;

    supabase
      .from('price_overrides')
      .select('product_id, prices')
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('useProducts: failed to fetch price overrides', error);
        } else {
          setProducts(mergeWithOverrides(baseProducts, data || []));
        }
        setLoading(false);
      });

    // ── Realtime: targeted patch instead of full re-fetch ──────
    // INSERT/UPDATE apply new prices directly; DELETE reverts to base.
    const channel = supabase
      .channel('price_overrides_changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'price_overrides' },
        (payload) => {
          if (cancelled) return;
          const { product_id, prices } = payload.new;
          setProducts((prev) =>
            prev.map((p) => p.id !== product_id ? p : { ...p, prices, price: Object.values(prices)[0] })
          );
        }
      )
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'price_overrides' },
        (payload) => {
          if (cancelled) return;
          const { product_id, prices } = payload.new;
          setProducts((prev) =>
            prev.map((p) => p.id !== product_id ? p : { ...p, prices, price: Object.values(prices)[0] })
          );
        }
      )
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'price_overrides' },
        (payload) => {
          if (cancelled) return;
          const base = baseProducts.find((b) => b.id === payload.old.product_id);
          if (!base) return;
          setProducts((prev) =>
            prev.map((p) => (p.id === payload.old.product_id ? { ...base } : p))
          );
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);

  const categories = ['All', ...new Set(products.map((p) => p.category))];

  return { products, categories, loading };
}

// ── Admin helpers (used by AdminPanel) ───────────────────────

export async function fetchOverrides() {
  const { data, error } = await supabase
    .from('price_overrides')
    .select('product_id, prices');
  if (error) throw error;
  // Return as { [product_id]: prices }
  const map = {};
  (data || []).forEach((row) => { map[row.product_id] = row.prices; });
  return map;
}

export async function saveOverride(productId, prices) {
  const { error } = await supabase
    .from('price_overrides')
    .upsert(
      { product_id: productId, prices, updated_at: new Date().toISOString() },
      { onConflict: 'product_id' }
    );
  if (error) throw error;
}

export async function deleteOverride(productId) {
  const { error } = await supabase
    .from('price_overrides')
    .delete()
    .eq('product_id', productId);
  if (error) throw error;
}

export async function resetAllOverrides() {
  const { error } = await supabase
    .from('price_overrides')
    .delete()
    .neq('product_id', 0); // delete all rows
  if (error) throw error;
}
